import { useEffect, useMemo, useState } from "react";

// MUI
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Stack,
	SvgIcon,
	TextField,
	Typography,
} from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridRenderCellParams, GridTreeNodeWithRender } from "@mui/x-data-grid-pro";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// Third Party
import clsx from "clsx";
import moment from "moment";
import { motion } from "framer-motion";

// Components
import NotesVersionHistoryTimeline from "./NotesVersionHistoryTimeline";

// Helpers
import { and, or } from "@/Common/helpers/sonar";

// Types
import { IReconciliation, IReconciliationNote, IReconciliationNoteHistory } from "@/DataPages/types/inventory/models/reconciliation";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as InformationIcon } from "@/assets/icons/common/circled_information.svg";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

// Animation Variants
const variants = {
	collapsed: { height: 0, opacity: 0, transition: { duration: 0.3 } },
	expanded: { height: "auto", opacity: 1, transition: { duration: 0.3 } },
};

const getOrderedNotes = (notes: IReconciliationNoteHistory[] | undefined) => {
	if (!notes) {
		return [];
	}

	return notes.sort((a, b) => (moment(a.createdAt).isBefore(moment(b.createdAt)) ? 1 : -1));
};

const getNoteInfo = (notes: IReconciliationNoteHistory[]) => {
	const orderedNotes = getOrderedNotes(notes);

	const firstNote = orderedNotes[orderedNotes.length - 1];
	const lastNote = orderedNotes[0];

	const createdAtText = moment(firstNote.createdAt).format(DATE_FORMAT.fullOrdinalTime12h);
	const modifiedAtText = moment(lastNote.createdAt).format(DATE_FORMAT.fullOrdinalTime12h);

	const createdBy = firstNote.createdBy ? `by ${firstNote.createdByFullName ?? firstNote.createdBy}` : "";
	const modifiedBy = lastNote.createdBy ? `by ${lastNote.createdByFullName ?? lastNote.createdBy}` : "";

	const createdByText = `Created ${createdBy} on ${createdAtText}`;
	const modifiedByText = `Last Modified ${modifiedBy} on ${modifiedAtText}`;

	return { createdByText, modifiedByText };
};

const getDefaultHistorySelected = (editable = false) => {
	if (!editable) {
		return 0;
	}
	return -1;
};

const getDefaultNote = (orderedDescNotes: IReconciliationNoteHistory[], editable = false) => {
	if (!editable) {
		return orderedDescNotes[0]?.note;
	}
	return "";
};

interface INotesDialogProps {
	id: string;
	editable?: boolean;
	isOpen: boolean;
	onCloseNotesDialog: () => void;
	title: string;
	params: GridRenderCellParams<any, IReconciliationNote, any, GridTreeNodeWithRender>;
	apiRef: React.MutableRefObject<GridApiPremium>;
}

const NotesDialog = (props: INotesDialogProps) => {
	const { editable, isOpen, onCloseNotesDialog, id, params, title, apiRef } = props;
	const { value, row } = params;
	const currentUser = useSelector((state: RootState) => state.user.user);

	// States
	const [isHistoryOpen, setIsHistoryOpen] = useState(false);
	const [originalRow, setOriginalRow] = useState<IReconciliation | null>(null);
	const [isMarkedAsCompleted, setIsMarkedAsCompleted] = useState(false);
	const [historySelected, setHistorySelected] = useState(-1);
	const [note, setNote] = useState("");

	const orderedDescNotes = useMemo(() => {
		if (!value?.notes) {
			return [];
		}
		const existSomeNote = value?.notes?.some((noteItem) => !!noteItem.note);
		if (!existSomeNote) {
			return [];
		}
		return getOrderedNotes(value.notes);
	}, [value]);

	const { createdByText, modifiedByText } = useMemo(() => {
		if (!value?.notes) {
			return { createdByText: "", modifiedByText: "" };
		}
		return getNoteInfo(value?.notes);
	}, [historySelected, value?.notes]);

	const handleOnSelectVersion = (selectedIndex: number) => {
		setHistorySelected(selectedIndex);
		const selected = orderedDescNotes[selectedIndex];
		if (!selected) {
			setHistorySelected(getDefaultHistorySelected(editable));
			setNote("");
			return;
		}
		setNote(selected.note);
	};
	const handleOnChangeViewHistory = () => {
		setIsHistoryOpen(!isHistoryOpen);
		if (!isHistoryOpen) {
			return;
		}
		setHistorySelected(getDefaultHistorySelected(editable));
		setNote("");
	};

	const handleOnChangeNote = (newNote: string) => {
		setNote(newNote);
	};

	const handlerOnAddToLog = () => {
		const filteredNotes = value?.notes?.filter((noteItem) => !!noteItem.note) || [];
		const notesUpdated = [...filteredNotes];

		const selected = orderedDescNotes[historySelected];

		const defaultUser = currentUser?.fullName ?? "";
		const newNote = {
			note,
			_id: selected ? selected._id : "",
			createdByFullName: selected ? selected.createdByFullName : defaultUser,
			createdBy: selected ? selected.createdBy : defaultUser,
			createdAt: moment().toDate(),
		};

		notesUpdated.push(newNote);

		apiRef.current.setEditCellValue({
			id,
			field: "notes",
			value: {
				...value,
				notes: notesUpdated,
			},
		});
		setHistorySelected(-1);
		setNote("");
	};

	const handleOnCloseDialog = () => {
		setIsHistoryOpen(false);
		setHistorySelected(getDefaultHistorySelected(editable));
		onCloseNotesDialog();
	};

	const handleMarkAsCompletedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		apiRef.current.setEditCellValue({
			id,
			field: "manuallyReconciled",
			value: e.target.checked,
		});
		setIsMarkedAsCompleted(e.target.checked);
	};

	const someNoteWasCreatedAfterLastUpdate =
		value && Array.isArray(value.notes)
			? value.notes.some((noteItem: IReconciliationNoteHistory) => moment(noteItem.createdAt).isAfter(row.reconciliationDate))
			: false;

	const disableTextArea = or(!editable, historySelected !== -1);
	const missingAllInformation = and(!row.reason, !row.resolution, !someNoteWasCreatedAfterLastUpdate);
	const disableCheckBox = or(!editable, !!originalRow?.reconciled, missingAllInformation);
	const disableAddToLogButton = or(historySelected !== -1, !editable, !note?.trim().length);
	const notesCount = orderedDescNotes.length ?? 0;

	useEffect(() => {
		setOriginalRow(row);
	}, [row]);

	useEffect(() => {
		setIsMarkedAsCompleted(row.reconciled);
	}, []);

	useEffect(() => {
		setNote(getDefaultNote(orderedDescNotes, editable));
		setHistorySelected(getDefaultHistorySelected(editable));
	}, [editable, orderedDescNotes]);

	return (
		<Dialog
			classes={{
				paper: "!rounded-[10px] !shadow-md p-4",
			}}
			open={isOpen}
			onClose={handleOnCloseDialog}
			PaperProps={{
				style: {
					minWidth: "580px",
					maxWidth: "580px",
				},
			}}
			disableEscapeKeyDown
			onDoubleClick={(e) => {
				e.stopPropagation();
			}}
		>
			<DialogTitle>
				<Typography className={"!font-montserrat !text-lg !font-medium !text-default uppercase"}>{title}</Typography>
				<IconButton className="!absolute !right-1 !top-1 !text-default" aria-label="close" onClick={handleOnCloseDialog}>
					<CloseRoundedIcon fontSize="medium" />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<Stack direction="column" justifyContent={"space-between"} alignItems={"center"} mt={2} width={"100%"} spacing={2}>
					<TextField
						multiline
						rows={5}
						placeholder="Type something here..."
						variant="outlined"
						autoComplete="off"
						disabled={disableTextArea}
						fullWidth
						spellCheck={false}
						InputProps={{ disableUnderline: true, className: "!font-sans !font-normal !text-sm !text-default !p-2 !pt-0" }}
						className="[&_.MuiOutlinedInput-notchedOutline]:!rounded-lg [&_.MuiOutlinedInput-notchedOutline]:!border-disabled"
						value={note}
						onChange={(e) => handleOnChangeNote(e.target.value)}
					/>
					<Stack direction="row" justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
						<FormControlLabel
							control={
								<Checkbox onChange={handleMarkAsCompletedChange} disabled={disableCheckBox} checked={isMarkedAsCompleted} />
							}
							label="Mark as Completed"
							classes={{ label: "!font-sans !text-sm !font-normal !text-default" }}
						/>
						<Button
							variant="contained"
							disableElevation
							className={clsx(
								"!h-[30px] !w-[126px] !rounded-md !font-sans !text-sm !text-white !font-normal !normal-case",
								disableAddToLogButton ? "!bg-disabled" : "!bg-primary"
							)}
							onClick={handlerOnAddToLog}
							disabled={disableAddToLogButton}
						>
							Add to Log
						</Button>
					</Stack>
					{notesCount > 0 ? (
						<>
							<Stack direction="row" justifyContent={"flex-start"} width={"100%"} spacing={0.5}>
								<Box>
									<SvgIcon
										className="!w-[20px] !h-[20px] ml-1 !fill-default"
										component={InformationIcon}
										viewBox="0 0 24 24"
									/>
								</Box>
								<Stack direction="column" width={"100%"}>
									{<Typography className="!font-sans !text-sm !font-normal !text-default">{createdByText}</Typography>}
									{<Typography className="!font-sans !text-sm !font-normal !text-default">{modifiedByText}</Typography>}
								</Stack>
							</Stack>
							{notesCount > 0 && (
								<Stack direction="column" width={"100%"}>
									<Typography
										className="!font-sans !text-sm !font-normal !text-primary !underline !decoration-primary !capitalize cursor-pointer"
										onClick={() => {
											handleOnChangeViewHistory();
										}}
									>
										{isHistoryOpen ? "Hide Log History" : "Show Log History"}
									</Typography>
								</Stack>
							)}
						</>
					) : null}
				</Stack>
				{notesCount > 0 && (
					<motion.div
						variants={variants}
						initial="collapsed"
						animate={isHistoryOpen ? "expanded" : "collapsed"}
						className="overflow-hidden"
					>
						<NotesVersionHistoryTimeline
							orderedDescNotes={orderedDescNotes}
							onSelected={handleOnSelectVersion}
							historySelected={historySelected}
							editable={editable}
						/>
					</motion.div>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default NotesDialog;
